import { getRouteApi } from '@tanstack/react-router'
import { useAssetImageData } from 'features/assetDatabase/hooks/useAssetImageData'
import { useAssetOverviewData } from 'features/assetDatabase/hooks/useAssetOverviewData'
import { useGetCustomSiteField } from 'features/assetDatabase/hooks/useGetCustomSideField'
import {
  AssetDataObjType,
  EditableComponentType,
  EditableFeatureType,
} from 'features/assetDatabase/types/assetOverviewTypes'
import { CustomField } from 'features/assetDatabase/types/customField'
import {
  createAssetComponentData,
  getAssetFormFormFields,
} from 'features/assetDatabase/utils/assetDBAssetsModels'
import { getLatestRiskAssessment } from 'features/fde'
import { useGeckoPermissions } from 'features/permissions'
import {
  useGetRolodexAuditLog,
  useGetRolodexEntriesByLoadOptions,
  useRolodexEntries,
} from 'features/rolodex'
import React, { createContext, useEffect, useMemo, useState } from 'react'
import {
  GeckoComponent,
  GeckoInspection,
  GeckoReport,
  GeckoRiskAssessment,
  GeckoRiskAssessmentConfig,
  GeckoUnit,
  PERMISSION_GECKO_INTERNAL_MEMBER,
  RolodexAuditLogObject,
  RolodexEntryType,
} from 'types'

export const HAS_MISSING_DETAILS_FIELDS = 0
export const HAS_MISSING_COMPONENTS_FIELDS = 1
export const HAS_MISSING_FEATURES_FIELDS = 2

interface AssetOverviewDataContextProps {
  components: readonly GeckoComponent[]
  unit: GeckoUnit | undefined
  customFields: CustomField[]
  inspections: readonly GeckoInspection[]
  isLoading: boolean
  hasEverLoaded: boolean
  refreshData: () => Promise<void>
  assetDataObj?: AssetDataObjType
  setAssetComponentData: React.Dispatch<
    React.SetStateAction<
      {
        componentData: EditableComponentType
        features: EditableFeatureType[]
      }[]
    >
  >
  assetThumbnails: readonly GeckoReport[]
  loadingThumbnails: boolean
  refreshThumbnails: () => Promise<void>
  isPrimaryImageLoaded: boolean
  setIsPrimaryImageLoaded: React.Dispatch<React.SetStateAction<boolean>>
  assetAuditLogs: RolodexAuditLogObject
  assetAuditLogsLoading: boolean
  latestRiskAssessment: GeckoRiskAssessment | undefined
  riskAssessmentConfig: GeckoRiskAssessmentConfig | undefined
}
//Context used to control and transform the fetched data from Rolodex for the Asset Overview Pages
export const AssetOverviewDataLayer: React.FC<{ children: React.ReactNode }> = (
  props
) => {
  const { children } = props
  const { useParams } = getRouteApi('/assets/$siteID/$unitID')
  const { unitID } = useParams()
  const {
    assetModel,
    components,
    inspections,
    unit,
    isLoading,
    hasEverLoaded,
    refreshData,
  } = useAssetOverviewData(unitID)

  const {
    assetThumbnails,
    isLoading: loadingThumbnails,
    refreshThumbnails,
  } = useAssetImageData(unitID)

  const [isPrimaryImageLoaded, setIsPrimaryImageLoaded] =
    useState<boolean>(false)

  const { siteID } = useParams()
  const site = useGetCustomSiteField(siteID ?? '')
  const customFields = useMemo(
    () =>
      site.data &&
      site.data.length > 0 &&
      site.data[0].entry.data.custom_fields &&
      site.data[0].entry.data.custom_fields.length > 0
        ? site.data[0].entry.data.custom_fields
        : [],
    [site.data]
  )
  const [assetComponentData, setAssetComponentData] = React.useState<
    {
      componentData: EditableComponentType
      features: EditableFeatureType[]
    }[]
  >([])

  useEffect(() => {
    if (assetModel) {
      setAssetComponentData(createAssetComponentData(assetModel))
    }
  }, [assetModel])

  const assetDataObj = useMemo(() => {
    if (unit) {
      const assetFormFields = getAssetFormFormFields(
        unit,
        customFields,
        assetModel
      )
      return {
        assetModel: assetModel,
        assetFormFields: assetFormFields,
        components: assetComponentData,
      }
    } else {
      return undefined
    }
  }, [assetModel, unit, customFields, assetComponentData])

  const { hasPermissions } = useGeckoPermissions()

  useEffect(() => {
    const appName = 'Gecko Cantilever'
    const unitName = unit?.data.name ?? null
    let title: string
    if (hasPermissions([PERMISSION_GECKO_INTERNAL_MEMBER]) && unitName) {
      title = `${unitName} ｜ ${appName}`
    } else {
      title = appName
    }
    document.title = title
  }, [hasPermissions, unit])

  // --------- Audit Log Data Fetching ---------
  const { data: entriesData, isLoading: entriesDataLoading } =
    useGetRolodexEntriesByLoadOptions<
      [
        {
          entry: {
            id: string
          }
        },
      ]
    >({
      type: [
        RolodexEntryType.COMPONENT,
        RolodexEntryType.ASSET_MODEL,
        RolodexEntryType.UNIT,
      ],
      tags: { unit: unitID as string },
    })

  const relevantIDs = entriesData
    ? entriesData.map((entry) => entry.entry.id)
    : []

  const { data: auditData, isLoading: auditDataFetching } =
    useGetRolodexAuditLog<RolodexAuditLogObject>(relevantIDs)

  const auditDataLoading = entriesDataLoading || auditDataFetching

  // --------- Risk Assessment Data Fetching ---------
  const { entries: riskAssessmentEntries } = useRolodexEntries(
    unitID && siteID
      ? {
          type: RolodexEntryType.RISK_ASSESSMENT,
          tags: {
            [RolodexEntryType.SITE]: siteID,
            [RolodexEntryType.UNIT]: unitID,
          },
        }
      : undefined
  )

  const { riskAssessmentConfigs } = useRolodexEntries(
    siteID
      ? {
          type: RolodexEntryType.RISK_ASSESSMENT_CONFIG,
          tags: { site: siteID },
          limit: 1,
        }
      : undefined
  )

  const latestRiskAssessment = useMemo(() => {
    const riskAssessments = riskAssessmentEntries as GeckoRiskAssessment[]
    return getLatestRiskAssessment(riskAssessments)
  }, [riskAssessmentEntries])

  const riskAssessmentConfig = useMemo(
    () => riskAssessmentConfigs.at(0),
    [riskAssessmentConfigs]
  )

  return (
    <AssetOverviewDataContext.Provider
      value={{
        components,
        inspections,
        unit,
        customFields,
        isLoading,
        hasEverLoaded,
        refreshData,
        assetDataObj,
        setAssetComponentData,
        assetThumbnails,
        loadingThumbnails,
        refreshThumbnails,
        isPrimaryImageLoaded,
        setIsPrimaryImageLoaded,
        assetAuditLogs: auditData || {},
        assetAuditLogsLoading: auditDataLoading,
        latestRiskAssessment,
        riskAssessmentConfig,
      }}>
      {children}
    </AssetOverviewDataContext.Provider>
  )
}

export const AssetOverviewDataContext =
  createContext<AssetOverviewDataContextProps>(null!)
